/**
 * Finanzas.
 *
 * Dos preguntas distintas que la pantalla no mezcla: **qué se facturó**
 * (devengo, sale de los cobros) y **qué entró** (caja, sale del ledger). El
 * ledger es append-only: un error se compensa, no se edita.
 */

import { useState } from 'react';
import {
  addMonths, formatMoney, parseMoneyInput, periodLabelShort, today,
  TXN_DIRECTION_LABEL, type TxnDirection,
} from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, Stat, Table,
} from '@/components/ui';

interface Monthly {
  /** `payroll_minor` es un SUBCONJUNTO de `outflow_minor`, no un sumando aparte. */
  cash: { month: string; inflow_minor: number; outflow_minor: number; payroll_minor: number }[];
  accrual: { month: string; billed_minor: number; charges: number }[];
  from: string;
}

interface Txn {
  id: string; occurred_on: string; direction: TxnDirection; amount_minor: number;
  memo: string | null; category_name: string | null; unit_code: string | null;
  party_name: string | null; source_type: string;
}

export function FinancePage() {
  const monthly = useApi<Monthly>('/admin/reports/monthly?months=12');
  const txns = useApi<{ transactions: Txn[] }>(
    `/admin/transactions?from=${addMonths(today(), -3)}`,
  );
  const [adding, setAdding] = useState(false);

  return (
    <>
      <PageHead
        title="Finanzas"
        subtitle="Ingresos por pago confirmado, egresos por compra o planilla. Nada se edita: se compensa."
        actions={<Button variant="primary" onClick={() => setAdding(true)}>Registrar egreso</Button>}
      />

      <Async query={monthly}>
        {(data) => {
          const last = data.cash.at(-1);
          const inflow = data.cash.reduce((a, m) => a + m.inflow_minor, 0);
          const outflow = data.cash.reduce((a, m) => a + m.outflow_minor, 0);
          const payroll = data.cash.reduce((a, m) => a + m.payroll_minor, 0);

          return (
            <>
              <div className="grid grid--4">
                <Stat accent label="Ingresos (12 meses)" value={<Money value={inflow} size="lg" />} />
                <Stat
                  label="Egresos (12 meses)"
                  value={<Money value={outflow} size="lg" />}
                  meta={payroll > 0
                    ? `incluye planilla ${Math.round((payroll / outflow) * 100)}%`
                    : 'sin planilla corrida'}
                />
                <Stat
                  label="Margen"
                  value={<Money value={inflow - outflow} size="lg" />}
                  meta={inflow > 0 ? `${Math.round(((inflow - outflow) / inflow) * 100)}%` : undefined}
                />
                <Stat
                  label="Este mes"
                  value={<Money value={last?.inflow_minor ?? 0} size="lg" />}
                  meta="cobrado"
                />
              </div>

              {/* Sin planilla corrida, el margen de arriba está inflado: falta el
                  egreso más grande del negocio. Se dice, no se deja adivinar. */}
              {payroll === 0 && (
                <div className="mt-2">
                  <Note tone="warn">
                    Todavía no hay ninguna planilla asentada, así que el margen no
                    descuenta salarios ni cargas sociales. Corrélo desde Personal → Planilla.
                  </Note>
                </div>
              )}

              <div className="mt-3">
                <Card title="Mes a mes" flush>
                  <Table head={
                    <tr>
                      <th>Mes</th><th className="right">Facturado</th>
                      <th className="right">Cobrado</th><th className="right">Planilla</th>
                      <th className="right">Egresos</th><th className="right">Neto</th>
                    </tr>
                  }>
                    {data.cash.map((m) => {
                      const accrual = data.accrual.find((a) => a.month === m.month);
                      return (
                        <tr key={m.month}>
                          <td className="strong">{periodLabelShort(`${m.month}-01`)}</td>
                          <td className="right">
                            <Money value={accrual?.billed_minor ?? 0} dim={!accrual} />
                          </td>
                          <td className="right"><Money value={m.inflow_minor} /></td>
                          <td className="right"><Money value={m.payroll_minor} dim={m.payroll_minor === 0} /></td>
                          <td className="right"><Money value={m.outflow_minor} dim={m.outflow_minor === 0} /></td>
                          <td className="right strong">
                            <Money value={m.inflow_minor - m.outflow_minor} />
                          </td>
                        </tr>
                      );
                    })}
                  </Table>
                </Card>
              </div>
            </>
          );
        }}
      </Async>

      <div className="mt-3">
        <Async query={txns} empty={(d) => d.transactions.length === 0 && (
          <Empty icon="reportes" title="Sin movimientos en los últimos tres meses" />
        )}>
          {(data) => (
            <Card title="Movimientos recientes" flush>
              <Table head={
                <tr><th>Fecha</th><th>Concepto</th><th>Consultorio</th><th className="right">Monto</th></tr>
              }>
                {data.transactions.map((t) => (
                  <tr key={t.id}>
                    <td className="small muted">{t.occurred_on}</td>
                    <td>
                      <Cell sub={t.category_name ?? t.source_type}>
                        {t.memo ?? TXN_DIRECTION_LABEL[t.direction]}
                      </Cell>
                    </td>
                    <td className="small muted">{t.unit_code ?? '—'}</td>
                    <td className="right">
                      <Badge tone={t.direction === 'INFLOW' ? 'ok' : 'neutral'} plain>
                        <Money value={t.direction === 'INFLOW' ? t.amount_minor : -t.amount_minor} />
                      </Badge>
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}
        </Async>
      </div>

      {adding && <ExpenseModal onClose={() => setAdding(false)} />}
    </>
  );
}

/**
 * Egreso manual.
 *
 * El campo se llena **en colones**, como todos los campos de dinero del sistema
 * (`parseMoneyInput` acepta `65000`, `65.000` y `₡65 000`), y el servidor recibe
 * céntimos. Antes pedía céntimos directo: un cero de más y quedaba un gasto cien
 * veces mayor asentado en un ledger que es append-only, o sea imposible de
 * borrar — solo compensable con el asiento contrario.
 */
function ExpenseModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ occurred_on: today(), amount: '', memo: '' });
  const amountMinor = form.amount ? parseMoneyInput(form.amount) : null;
  const mutation = useApiMutation(
    () => api.post('/admin/expenses', {
      occurred_on: form.occurred_on, amount: amountMinor, memo: form.memo,
    }),
    ['/admin/transactions', '/admin/reports'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title="Registrar egreso"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary"
            disabled={!amountMinor || amountMinor <= 0 || !form.memo || mutation.isPending}
            onClick={() => mutation.mutate()}>
            Registrar
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Note tone="info">
        Los egresos de compras se crean solos al registrar la compra. Este es para lo demás.
      </Note>
      <div className="mt-2">
        <Field label="Fecha" required>
          <input className="input" type="date" value={form.occurred_on}
            onChange={(e) => setForm((f) => ({ ...f, occurred_on: e.target.value }))} />
        </Field>
        <Field
          label="Monto (₡)"
          required
          hint={amountMinor
            ? `Se registra ${formatMoney(amountMinor)}.`
            : 'En colones: 65000 o 65.000.'}
        >
          <input className="input input--money" type="text" inputMode="numeric" placeholder="₡0"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
        </Field>
        <Field label="Concepto" required>
          <input className="input" value={form.memo}
            onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))} />
        </Field>
      </div>
    </Modal>
  );
}
